/**
 * The preview's two no-model editors: point at an element and change its text,
 * and change the site's design tokens. Both write the SOURCE file through the
 * ordinary `fs.read`/`fs.write` channels and then reload the preview, so what
 * is shown is always what is on disk. Anything they cannot do safely is handed
 * to the agent as a ready-made request instead (`onAsk`).
 */
import { el } from "./dom.js";
import { type EditResult, type DesignToken, isHexColor, previewPagePath, readTokens, replaceUniqueText, setTextById, syncContractPalette, writeTokens } from "./siteEdit.js";

export interface PickedElement {
  readonly id: string | null;
  readonly tag: string;
  readonly text: string;
  readonly leaf: boolean;
  readonly path: string;
}

export interface FileIo {
  read(path: string): Promise<string>;
  write(path: string, content: string): Promise<void>;
}

function isPicked(d: unknown): d is PickedElement & { type: "picked" } {
  if (typeof d !== "object" || d === null) return false;
  const r = d as Record<string, unknown>;
  return r["source"] === "tavsin-preview" && r["type"] === "picked" && typeof r["tag"] === "string" && typeof r["text"] === "string" && typeof r["path"] === "string";
}

/** Listen for picks from THIS frame only. Returns the unsubscribe. */
export function listenForPicks(frame: HTMLIFrameElement, onPick: (p: PickedElement) => void, onCancel: () => void): () => void {
  const handler = (e: MessageEvent): void => {
    if (e.source !== frame.contentWindow) return;
    const d: unknown = e.data;
    if (isPicked(d)) onPick({ id: typeof d.id === "string" ? d.id : null, tag: d.tag, text: d.text, leaf: d.leaf === true, path: d.path });
    else if (typeof d === "object" && d !== null && (d as Record<string, unknown>)["source"] === "tavsin-preview" && (d as Record<string, unknown>)["type"] === "pick-cancel") onCancel();
  };
  window.addEventListener("message", handler);
  return () => window.removeEventListener("message", handler);
}

export function setPicking(frame: HTMLIFrameElement, on: boolean): void {
  frame.contentWindow?.postMessage({ source: "tavsin", type: "pick", on }, "*");
}

/** Change a picked element's text in its source file. */
export async function applyPickedText(picked: PickedElement, text: string, io: FileIo): Promise<EditResult & { path: string }> {
  const path = previewPagePath(picked.path);
  let html: string;
  try {
    html = await io.read(path);
  } catch (e) {
    return { ok: false, reason: e instanceof Error ? e.message : String(e), path };
  }
  const result = picked.id ? setTextById(html, picked.id, text) : picked.leaf ? replaceUniqueText(html, picked.text, text) : { ok: false as const, reason: "that element contains other elements — ask Tavsin to change it" };
  if (!result.ok) return { ...result, path };
  await io.write(path, result.content);
  return { ...result, path };
}

/** The editor shown when an element is picked. */
export function pickedEditor(picked: PickedElement, opts: { io: FileIo; onDone: (changedPath: string | null) => void; onAsk: (request: string) => void }): HTMLElement {
  const label = `${picked.tag}${picked.id ? ` · ${picked.id}` : ""}`;
  const status = el("p", { class: "vedit-status", role: "status" });
  const canEditText = picked.leaf && picked.text.length > 0;
  const input = el("textarea", { class: "vedit-input", rows: "3", "aria-label": "Text" }) as HTMLTextAreaElement;
  input.value = picked.text;
  const ask = el("input", { class: "vedit-ask", type: "text", placeholder: "Or describe a change (spacing, colour, layout)…", "aria-label": "Describe a change for Tavsin" }) as HTMLInputElement;
  const save = el("button", { class: "btn btn-primary", type: "button" }, ["Save text"]);
  const send = el("button", { class: "btn", type: "button" }, ["Ask Tavsin"]);
  const cancel = el("button", { class: "btn btn-ghost", type: "button" }, ["Close"]);
  save.onclick = async () => {
    save.setAttribute("disabled", "");
    const r = await applyPickedText(picked, input.value, opts.io).catch((e: unknown) => ({ ok: false as const, reason: e instanceof Error ? e.message : String(e), path: "" }));
    save.removeAttribute("disabled");
    if (r.ok) opts.onDone(r.path);
    else status.textContent = `Not changed: ${r.reason}.`;
  };
  send.onclick = () => {
    const what = ask.value.trim() || (canEditText && input.value.trim() !== picked.text ? `change its text to "${input.value.trim()}"` : "");
    if (!what) {
      status.textContent = "Describe the change first.";
      return;
    }
    const where = previewPagePath(picked.path);
    const quoted = picked.text ? ` (the one that reads "${picked.text.slice(0, 160)}")` : "";
    opts.onAsk(`In ${where}, on the <${picked.tag}>${picked.id ? ` with data-tavsin-id="${picked.id}"` : ""}${quoted}: ${what}. Change only that element and keep the design tokens.`);
    opts.onDone(null);
  };
  cancel.onclick = () => opts.onDone(null);
  return el("div", { class: "vedit", role: "dialog", "aria-label": `Edit ${label}` }, [
    el("div", { class: "vedit-head" }, [el("span", { class: "vedit-tag" }, [label])]),
    ...(canEditText ? [input] : [el("p", { class: "vedit-note" }, ["This element holds other elements; describe the change and Tavsin will make it."])]),
    ask,
    status,
    el("div", { class: "vedit-actions" }, [cancel, send, ...(canEditText ? [save] : [])]),
  ]);
}

/** Where a generated site keeps its tokens, most specific first. */
export const TOKEN_FILES = ["styles/tokens.css", "tokens.css", "css/tokens.css", "styles/base.css", "styles.css", "style.css", "css/styles.css", "css/style.css", "styles/site.css"] as const;

/** Find the first stylesheet with tokens on `:root`. */
export async function findTokenFile(io: FileIo): Promise<{ path: string; css: string; tokens: DesignToken[] } | null> {
  for (const path of TOKEN_FILES) {
    let css: string;
    try {
      css = await io.read(path);
    } catch {
      continue;
    }
    const tokens = readTokens(css);
    if (tokens.length > 0) return { path, css, tokens };
  }
  return null;
}

/** The theme panel: one control per token, written back on Apply. */
export function themeEditor(found: { path: string; css: string; tokens: DesignToken[] }, opts: { io: FileIo; onSaved: () => void; onClose: () => void }): HTMLElement {
  const edits = new Map<string, string>();
  const status = el("p", { class: "vedit-status", role: "status" });
  const rows = found.tokens.slice(0, 40).map((t) => {
    const color = isHexColor(t.value);
    const input = el("input", { class: color ? "theme-color" : "theme-text", type: color ? "color" : "text", value: color ? expandHex(t.value) : t.value, "aria-label": t.name }) as HTMLInputElement;
    input.oninput = () => edits.set(t.name, input.value);
    return el("label", { class: "theme-row" }, [el("code", {}, [t.name]), input]);
  });
  const apply = el("button", { class: "btn btn-primary", type: "button" }, ["Apply"]);
  apply.onclick = async () => {
    if (edits.size === 0) {
      opts.onClose();
      return;
    }
    let css: string;
    try {
      css = await opts.io.read(found.path);
    } catch (e) {
      status.textContent = e instanceof Error ? e.message : String(e);
      return;
    }
    const r = writeTokens(css, Object.fromEntries(edits));
    if (!r.ok) {
      status.textContent = `Not changed: ${r.reason}.`;
      return;
    }
    await opts.io.write(found.path, r.content);
    // The design contract lists the palette; keep it in step, or the agent's
    // next edit would be refused for using the colour just chosen here.
    try {
      const md = await opts.io.read("tavsin/design.md");
      const next = syncContractPalette(md, Object.fromEntries(edits));
      if (next !== md) await opts.io.write("tavsin/design.md", next);
    } catch {
      /* no contract in this project */
    }
    opts.onSaved();
  };
  const close = el("button", { class: "btn btn-ghost", type: "button" }, ["Close"]);
  close.onclick = opts.onClose;
  return el("div", { class: "vedit theme-panel", role: "dialog", "aria-label": "Theme" }, [
    el("div", { class: "vedit-head" }, [el("span", { class: "vedit-tag" }, [`Theme · ${found.path}`])]),
    el("p", { class: "vedit-note" }, ["Changes the site's design tokens directly. No model call."]),
    el("div", { class: "theme-rows" }, rows),
    status,
    el("div", { class: "vedit-actions" }, [close, apply]),
  ]);
}

function expandHex(v: string): string {
  const s = v.trim();
  return s.length === 4 ? `#${s[1]}${s[1]}${s[2]}${s[2]}${s[3]}${s[3]}` : s;
}
